import { Table, TableBody, Tooltip } from '@mui/material'
import cn from 'classnames'
import moment from 'moment'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ErrorNote from '../common/util/ErrorNote'
import { getAccountInfo, post } from '../helpers/Api'
import {
    formatHoney,
    formatNumber,
    formatRAM,
    formatWAX,
} from '../helpers/FormatLinks'
import {
    getAtomicBalance,
    getBoostAction,
    getClaimableHoneyBalance,
    getHoneyBalance,
    getStakedHoneyBalance,
    getWaxDaoBackedTokens,
} from '../helpers/WaxApi'
import { useSharedState } from '../waxplorer/Waxplorer'
import { WalletRow } from './WalletRow'

const Wallet = () => {
    const [state, dispatch] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null
    const [atomicBalance, setAtomicBalance] = useState(0)
    const [updateCount, setUpdateCount] = useState(0)
    const [isLoadingStake, setIsLoadingStake] = useState(false)
    const [error, setError] = useState(null)
    const [rewardedTokens, setRewardedTokens] = useState(null)
    const [waxdaoTokens, setWaxdaoTokens] = useState([])
    const [stakedHoneyBalance, setStakedHoneyBalance] = useState(0)
    const [honeyBalance, setHoneyBalance] = useState(null)
    const [claimableHoneyBalance, setClaimableHoneyBalance] = useState(null)

    const [account, setAccount] = useState({
        netWeight: 0,
        cpuWeight: 0,
        ramBytes: 0,
        cpu: {
            used: 0,
            available: 0,
            max: 0,
        },
        net: {
            used: 0,
            available: 0,
            max: 0,
        },
        balance: 0,
        ram: {
            quota: 0,
            usage: 0,
        },
    })

    const { t } = useTranslation('common')

    useEffect(() => {}, [updateCount])

    const parseAccount = (res) => {
        if (res && res.status === 200) {
            const data = res['data']

            const resources = data['self_delegated_bandwidth']

            const balance = data['core_liquid_balance']
                ? data['core_liquid_balance'].replace(' WAX', '')
                : '0'

            const cpu = data['cpu_limit']

            const net = data['net_limit']

            const ram = {
                quota: data['ram_quota'],
                usage: data['ram_usage'],
            }

            const refund = data['refund_request']
                ? {
                      refundDate: moment
                          .utc(data['refund_request']['request_time'])
                          .add(3, 'days'),
                      netAmount: data['refund_request']['net_amount']
                          ? parseFloat(
                                data['refund_request']['net_amount'].replace(
                                    ' WAX',
                                    '',
                                ),
                            )
                          : 0,
                      cpuAmount: data['refund_request']['cpu_amount']
                          ? parseFloat(
                                data['refund_request']['cpu_amount'].replace(
                                    ' WAX',
                                    '',
                                ),
                            )
                          : 0,
                  }
                : null

            setAccount({
                netWeight: parseFloat(
                    resources && resources['net_weight']
                        ? resources['net_weight'].replace(' WAX', '')
                        : 0,
                ),
                cpuWeight: parseFloat(
                    resources && resources['cpu_weight']
                        ? resources['cpu_weight'].replace(' WAX', '')
                        : 0,
                ),
                ramBytes:
                    resources && resources['ram_bytes']
                        ? resources['ram_bytes']
                        : 0,
                cpu: cpu,
                net: net,
                balance: balance,
                ram: ram,
                refund: refund,
            })
        }
        setUpdateCount(updateCount + 1)
    }

    const parseAtomicBalance = (res) => {
        if (res && res.status === 200) {
            let atomic = 0
            const data = res.data

            if (data && Object.keys(data).includes('rows'))
                data['rows'].map((row) => {
                    if (Object.keys(row).includes('quantities'))
                        row['quantities'].map((quantity) => {
                            if (quantity.includes(' WAX')) {
                                atomic += parseFloat(
                                    quantity.replace(' WAX', ''),
                                )
                            }
                        })
                })

            setUpdateCount(updateCount + 1)
            setAtomicBalance(atomic)
        }
    }

    const parseHoneyBalance = (res) => {
        if (res && res.status === 200) {
            let balance = 0
            const data = res.data

            if (data && Object.keys(data).includes('rows'))
                data['rows'].map((row) => {
                    if (row['balance'].includes(' HONEY'))
                        balance += parseFloat(
                            row['balance'].replace(' HONEY', ''),
                        )
                })

            setUpdateCount(updateCount + 1)
            setHoneyBalance(balance)
        }
    }

    const parseStakedHoneyBalance = (res) => {
        if (res && res.status === 200) {
            const data = res.data

            if (
                data &&
                Object.keys(data).includes('rows') &&
                data['rows'].length === 1
            ) {
                const row = data['rows'][0]
                if (
                    row['staker'] === userName &&
                    row['amount'].includes(' HONEY')
                ) {
                    setStakedHoneyBalance(row['amount'].replace(' HONEY', ''))
                    const tokens = []
                    for (const token of row['rewarded_tokens']) {
                        const amount = parseFloat(token.split(' ')[0])
                        if (amount > 0) {
                            tokens.push(token)
                        }
                    }
                    setRewardedTokens(tokens)
                } else {
                    setStakedHoneyBalance(0)
                }
            } else {
                setStakedHoneyBalance(0)
            }

            setUpdateCount(updateCount + 1)
        }
    }

    const parseWaxdaoBalance = (res) => {
        if (res && res.status === 200) {
            const data = res.data

            if (
                data &&
                Object.keys(data).includes('rows') &&
                data['rows'].length === 1
            ) {
                const tokens = []
                for (const token of data['rows'][0]['balances']) {
                    tokens.push(token['quantity'])
                }
                setWaxdaoTokens(tokens)
            }

            setUpdateCount(updateCount + 1)
        }
    }

    const parseClaimableHoneyBalance = (res) => {
        if (
            res &&
            res.status === 200 &&
            res.data &&
            res.data.rows &&
            res.data.rows.length > 0 &&
            res.data.rows[0].user === userName &&
            res.data.rows[0].rewards
        ) {
            setClaimableHoneyBalance(
                res.data.rows[0].rewards.replace(' HONEY', ''),
            )
        } else {
            setClaimableHoneyBalance(0)
        }
    }

    const getUserBalances = async (name) => {
        if (typeof window !== 'undefined') {
            getAccountInfo(name, state).then(parseAccount)
            getAtomicBalance(name, state).then(parseAtomicBalance)
            getHoneyBalance(name, state).then(parseHoneyBalance)
            getClaimableHoneyBalance(name, state).then(
                parseClaimableHoneyBalance,
            )
            getStakedHoneyBalance(name, state).then(parseStakedHoneyBalance)
            getWaxDaoBackedTokens(name, state).then(parseWaxdaoBalance)
        }
    }

    useEffect(() => {
        if (userName) {
            getUserBalances(userName)
        }
    }, [userName])

    useEffect(() => {}, [claimableHoneyBalance, atomicBalance, honeyBalance])

    const claimAtomic = async (quantity) => {
        return await activeUser.session.transact(
            {
                actions: [
                    getBoostAction(activeUser),
                    {
                        account: 'atomicmarket',
                        name: 'withdraw',
                        authorization: [
                            {
                                actor: userName,
                                permission: activeUser.requestPermission
                                    ? activeUser.requestPermission
                                    : 'active',
                            },
                        ],
                        data: {
                            owner: userName,
                            token_to_withdraw: `${quantity.toFixed(8)} WAX`,
                        },
                    },
                ],
            },
            {
                expireSeconds: 300,
                blocksBehind: 0,
            },
        )
    }

    const refund = async () => {
        try {
            await activeUser.session.transact(
                {
                    actions: [
                        getBoostAction(activeUser),
                        {
                            account: 'eosio',
                            name: 'refund',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                owner: userName,
                            },
                        },
                    ],
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )
        } catch (e) {
            console.error(e)
        } finally {
            setTimeout(function () {
                getAccountInfo(userName, state).then(parseAccount)
            }, 3000)
        }
    }

    const handleStake = (stakeInfo) => {
        const wasStaked = stakeInfo['wasStaked']
        const error = stakeInfo['error']

        if (wasStaked) {
            setTimeout(function () {
                getAccountInfo(userName, state).then(parseAccount)
                setIsLoadingStake(false)
            }, 3000)
        } else {
            setIsLoadingStake(false)
        }

        if (error) {
            setIsLoadingStake(false)
        }

        setError(error)
    }

    const stakeCpuNet = async () => {
        setIsLoadingStake(true)
        dispatch({ type: 'SET_WAX_BALANCE', payload: account.balance })
        dispatch({ type: 'SET_ACTION', payload: 'stake_cpu_net' })
        dispatch({
            type: 'SET_CALLBACK',
            payload: (stakeInfo) => handleStake(stakeInfo),
        })
        dispatch({ type: 'SET_BALANCE_CHANGED', payload: true })
    }

    const unstakeCpuNet = async () => {
        setIsLoadingStake(true)
        dispatch({ type: 'SET_CPU', payload: account.cpuWeight })
        dispatch({ type: 'SET_NET', payload: account.netWeight })
        dispatch({ type: 'SET_ACTION', payload: 'unstake_cpu_net' })
        dispatch({
            type: 'SET_CALLBACK',
            payload: (stakeInfo) => handleStake(stakeInfo),
        })
    }

    const claimWaxdaoTokens = async () => {
        return await activeUser.session.transact(
            {
                actions: [
                    getBoostAction(activeUser),
                    {
                        account: 'waxdaobacker',
                        name: 'withdraw',
                        authorization: [
                            {
                                actor: userName,
                                permission: activeUser.requestPermission
                                    ? activeUser.requestPermission
                                    : 'active',
                            },
                        ],
                        data: {
                            user: userName,
                            contract_ignore_list: [],
                        },
                    },
                ],
            },
            {
                expireSeconds: 300,
                blocksBehind: 0,
            },
        )
    }

    const claimRewardedTokens = async () => {
        return await activeUser.session.transact(
            {
                actions: [
                    getBoostAction(activeUser),
                    {
                        account: 'nfthivevault',
                        name: 'claimstake',
                        authorization: [
                            {
                                actor: userName,
                                permission: activeUser.requestPermission
                                    ? activeUser.requestPermission
                                    : 'active',
                            },
                        ],
                        data: {
                            claimer: userName,
                        },
                    },
                ],
            },
            {
                expireSeconds: 300,
                blocksBehind: 0,
            },
        )
    }

    const claimHoney = async () => {
        return await activeUser.session.transact(
            {
                actions: [
                    getBoostAction(activeUser),
                    {
                        account: 'nfthivevault',
                        name: 'claim',
                        authorization: [
                            {
                                actor: userName,
                                permission: activeUser.requestPermission
                                    ? activeUser.requestPermission
                                    : 'active',
                            },
                        ],
                        data: {
                            claimer: userName,
                        },
                    },
                ],
            },
            {
                expireSeconds: 300,
                blocksBehind: 0,
            },
        )
    }

    const hasRefund =
        account &&
        account.refund &&
        (account.refund.cpuAmount || account.refund.netAmount)

    const refundAvailable =
        hasRefund && account.refund.refundDate.isBefore(moment.utc())

    return (
        <div
            className={cn(
                'w-full overflow-x-auto overflow-hidden relative max-h-140',
                'overflow-y-auto InsideScroll',
            )}
        >
            {error && (
                <ErrorNote error={error} onClick={() => setError(null)} />
            )}
            <Table
                size="small"
                sx={{ width: '100%' }}
                className={'p-4 rounded-xl mx-auto overflow-x-auto max-w-240'}
            >
                <TableBody>
                    <WalletRow
                        tokenIcon={'/tokens/wax_eosio.token.png'}
                        title={`WAX ${t('navigation.balance')}`}
                        content={formatWAX(account.balance)}
                        balance={account.balance}
                        token={'WAX'}
                        name={'wallet_balance'}
                        updateFunc={() => {
                            dispatch({
                                type: 'SET_BALANCE_CHANGED',
                                payload: true,
                            })
                            getAccountInfo(userName, state).then(parseAccount)
                        }}
                    />
                    <WalletRow
                        title={`HONEY ${t('navigation.balance')}`}
                        tokenIcon={'/tokens/honey_nfthivehoney.png'}
                        content={honeyBalance ? formatHoney(honeyBalance) : 0}
                        token={'HONEY'}
                        balance={honeyBalance}
                        name={'wallet_honey_balance'}
                        updateFunc={() => {
                            dispatch({
                                type: 'SET_HONEY_BALANCE_CHANGED',
                                payload: true,
                            })
                            getHoneyBalance(userName, state).then(
                                parseHoneyBalance,
                            )
                            getStakedHoneyBalance(userName, state).then(
                                parseStakedHoneyBalance,
                            )
                        }}
                        stakable={true}
                    />
                    <WalletRow
                        title={`Claimable HONEY`}
                        tokenIcon={'/tokens/honey_nfthivehoney.png'}
                        content={formatHoney(
                            claimableHoneyBalance ? claimableHoneyBalance : 0,
                        )}
                        hasClaim={true}
                        balance={claimableHoneyBalance}
                        name={'wallet_claimable_honey_balance'}
                        claimFunc={claimHoney}
                        updateFunc={() => {
                            dispatch({
                                type: 'SET_HONEY_BALANCE_CHANGED',
                                payload: true,
                            })
                            getHoneyBalance(userName, state).then(
                                parseHoneyBalance,
                            )
                            getClaimableHoneyBalance(userName, state).then(
                                parseClaimableHoneyBalance,
                            )
                            getStakedHoneyBalance(userName, state).then(
                                parseStakedHoneyBalance,
                            )
                        }}
                    />
                    <WalletRow
                        title={`Staked HONEY`}
                        tokenIcon={'/tokens/honey_nfthivehoney.png'}
                        content={formatHoney(
                            stakedHoneyBalance ? stakedHoneyBalance : 0,
                        )}
                        staked={true}
                        hasClaim={false}
                        name={'wallet_staked_honey_balance'}
                        balance={stakedHoneyBalance}
                        updateFunc={() => {
                            dispatch({
                                type: 'SET_HONEY_BALANCE_CHANGED',
                                payload: true,
                            })
                            getHoneyBalance(userName, state).then(
                                parseHoneyBalance,
                            )
                            getStakedHoneyBalance(userName, state).then(
                                parseStakedHoneyBalance,
                            )
                        }}
                    />
                    {rewardedTokens && rewardedTokens.length > 0 && (
                        <WalletRow
                            title={`Rewarded Tokens`}
                            content={
                                <div className={'flex flex-wrap justify-end'}>
                                    {rewardedTokens.map((token) => (
                                        <div className={'mx-1'}>{token}</div>
                                    ))}
                                </div>
                            }
                            name={'wallet_rewarded_tokens'}
                            hasClaim={true}
                            balance={rewardedTokens}
                            claimFunc={claimRewardedTokens}
                            updateFunc={() => {
                                dispatch({
                                    type: 'SET_HONEY_BALANCE_CHANGED',
                                    payload: true,
                                })
                                getStakedHoneyBalance(userName, state).then(
                                    parseStakedHoneyBalance,
                                )
                                getAccountInfo(userName, state).then(
                                    parseAccount,
                                )
                            }}
                        />
                    )}
                    {waxdaoTokens && waxdaoTokens.length > 0 && (
                        <WalletRow
                            title={`WaxDAO Balance`}
                            tokenIcon={'/waxdao.svg'}
                            content={
                                <div className={'flex flex-wrap justify-end'}>
                                    {waxdaoTokens.map((token) => (
                                        <div className={'mx-1'}>{token}</div>
                                    ))}
                                </div>
                            }
                            name={'wallet_waxdao_backed_tokens'}
                            hasClaim={true}
                            balance={waxdaoTokens}
                            claimFunc={claimWaxdaoTokens}
                            updateFunc={() => {
                                getWaxDaoBackedTokens(userName, state).then(
                                    parseWaxdaoBalance,
                                )
                            }}
                        />
                    )}
                    <WalletRow
                        title={`AtomicMarket ${t('navigation.balance')}`}
                        tokenIcon={'/atomic.png'}
                        content={formatWAX(atomicBalance)}
                        hasClaim={true}
                        name={'wallet_atomicmarket_balance'}
                        updateFunc={() => {
                            getAtomicBalance(userName).then(parseAtomicBalance)
                            dispatch({
                                type: 'SET_BALANCE_CHANGED',
                                payload: true,
                            })
                            getAccountInfo(userName, state).then(parseAccount)
                        }}
                        balance={atomicBalance}
                        claimFunc={() => claimAtomic(atomicBalance)}
                    />
                    <WalletRow
                        title={'CPU Staked'}
                        content={`${formatNumber(account.cpuWeight)} WAX`}
                        externalFunc={unstakeCpuNet}
                        balance={account.cpuWeight}
                        name={'wallet_staked_cpu'}
                        buttonText={t('asset.unstake')}
                        externalLoading={isLoadingStake}
                    />
                    <WalletRow
                        title={'CPU Usage'}
                        progressBar={true}
                        progress={
                            account.cpu.max > 0
                                ? account.cpu.used / account.cpu.max
                                : 0
                        }
                        color={'bg-card2'}
                        name={'wallet_cpu_usage'}
                        externalFunc={stakeCpuNet}
                        balance={account.balance}
                        buttonText={t('asset.stake')}
                        externalLoading={isLoadingStake}
                    />
                    <WalletRow
                        title={'NET Staked'}
                        content={formatNumber(account.netWeight)}
                        externalFunc={unstakeCpuNet}
                        name={'wallet_net_staked'}
                        balance={account.netWeight}
                        buttonText={t('asset.unstake')}
                        externalLoading={isLoadingStake}
                    />
                    <WalletRow
                        title={'NET Usage'}
                        progressBar={true}
                        progress={
                            account.net.max > 0
                                ? account.net.used / account.net.max
                                : 0
                        }
                        externalFunc={stakeCpuNet}
                        name={'wallet_net_usage'}
                        balance={account.balance}
                        buttonText={t('asset.stake')}
                        externalLoading={isLoadingStake}
                    />
                    <WalletRow
                        title={'RAM:'}
                        name={'wallet_ram_balance'}
                        content={formatRAM(account.ram.quota)}
                    />
                    {hasRefund && (
                        <WalletRow
                            title={'Refund'}
                            content={
                                <Tooltip
                                    title={
                                        'Available: ' +
                                        account.refund.refundDate
                                            .toDate()
                                            .toLocaleString()
                                    }
                                >
                                    <div className={'w-full'}>
                                        {formatWAX(
                                            account.refund.cpuAmount +
                                                account.refund.netAmount,
                                        )}
                                    </div>
                                </Tooltip>
                            }
                            name={'wallet_refund'}
                            balance={refundAvailable}
                            hasClaim={true}
                            buttonText={'Refund'}
                            claimFunc={refund}
                        />
                    )}
                    <WalletRow
                        title={'RAM Usage:'}
                        name={'wallet_ram_usage'}
                        progressBar={true}
                        progress={
                            account.ram.quota > 0
                                ? account.ram.usage / account.ram.quota
                                : 0
                        }
                        color={'bg-card6'}
                    />
                </TableBody>
            </Table>
        </div>
    )
}

export default Wallet
